import React, { useCallback, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Grid } from '@atlaskit/primitives/compiled/grid';
import { Text } from '@atlaskit/primitives/compiled/text';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading/heading';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import CrossIcon from '@atlaskit/icon/core/cross';
import { useResizingHeight } from '@atlaskit/motion/height';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

import { usePTV2Store } from '../usePTV2Store.tsx';
import { teamToTemplatesMapping } from '../mappings/teamToTemplatesMapping.ts';

export const i18n: {
	heading: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	subheading: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	label: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	expand: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	collapse: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	heading: {
		id: 'experiment-personalized-templates-v2.nth-user.template.heading',
		defaultMessage: 'Templates trusted by {teamType} teams like yours',
		description:
			'The text is shown as a heading above the template list on the home onboarding screen for nth users. The placeholder {teamType} will be substituted with the user\'s team type (e.g. "engineering", "marketing", "design").',
	},
	subheading: {
		id: 'experiment-personalized-templates-v2.nth-user.template.subheading',
		defaultMessage: 'Chosen to help you move fast and make an impact from day one',
		description:
			'The text is shown as a subheading below the main heading on the home onboarding template list screen for nth users. It encourages users to get started quickly with a recommended template.',
	},
	label: {
		id: 'experiment-personalized-templates-v2.nth-user.template.dismiss.label',
		defaultMessage: 'Dismiss recommendations',
		description:
			'Label for a close icon button. When clicked, this button dismisses a row of suggested templates',
	},
	expand: {
		id: 'experiment-personalized-templates-v2.nth-user.template.see.more.templates.button',
		defaultMessage: 'Show {numberTemplateSuggestions} more',
		description: 'Button that will show a certain number of more templates. Ex See 4 more',
	},
	collapse: {
		id: 'experiment-personalized-templates-v2.nth-user.template.show.fewer.button',
		defaultMessage: 'Show fewer',
		description: 'Button label to collapse the expanded template list and show fewer templates',
	},
});

const styles = cssMap({
	gridStyles: {
		gridTemplateColumns: 'repeat(calc(4 - var(--homeIsSingleColumn, 0)), 1fr)',
	},
	textContainerStyles: {
		paddingBottom: token('space.150'),
	},
	listResetStyles: {
		listStyle: 'none',
		marginBlockStart: token('space.0'),
		marginBlockEnd: token('space.0'),
		marginInlineStart: token('space.0'),
		marginInlineEnd: token('space.0'),
		paddingInlineStart: token('space.0'),
	},
});

type NthUserTemplateListProps = {
	cards: Array<React.JSX.Element>;
	teamType: string;
	shouldShowUseCasesHomeWelcomeBanner?: boolean;
};

const SOURCE = 'nth-user-home-onboarding-revamp';

export const NthUserTemplateList = ({
	cards,
	teamType,
	shouldShowUseCasesHomeWelcomeBanner,
}: NthUserTemplateListProps): React.JSX.Element => {
	const [{ onboardingPeriodDay }, { setIsTemplateListDismissed }] = usePTV2Store();
	const [isTemplateListExpanded, setIsTemplateExpanded] = useState(false);
	const { formatMessage } = useIntl();
	const resizingHeight = useResizingHeight();
	const remainingTemplates = (cards?.length || 0) - 4;
	const expandButtonMessage = isTemplateListExpanded
		? formatMessage(i18n.collapse)
		: formatMessage(i18n.expand, {
				numberTemplateSuggestions: remainingTemplates,
			});
	const { setOnboardingState } = useSetOnboardingState();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const personalizationHeuristic = shouldShowUseCasesHomeWelcomeBanner
		? 'useCases'
		: teamToTemplatesMapping[teamType.toLocaleUpperCase()]
			? 'teamType'
			: 'popular';

	const handleToggleButtonClick = useCallback(() => {
		setIsTemplateExpanded(!isTemplateListExpanded);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'openCloseHomeRevampTemplates',
				source: SOURCE,
				attributes: {
					isExpanding: !isTemplateListExpanded,
					personalizationHeuristic,
					onboardingPeriodDay,
				},
			},
		}).fire();
	}, [
		isTemplateListExpanded,
		setIsTemplateExpanded,
		createAnalyticsEvent,
		personalizationHeuristic,
		onboardingPeriodDay,
	]);

	const handleDismissTemplateListClick = useCallback(() => {
		setOnboardingState({
			key: NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS.isTemplateListDismissed,
			value: 'true',
		});
		setIsTemplateListDismissed(true);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissHomeRevampTemplates',
				source: SOURCE,
				attributes: { personalizationHeuristic, onboardingPeriodDay },
			},
		}).fire();
	}, [
		setOnboardingState,
		setIsTemplateListDismissed,
		createAnalyticsEvent,
		personalizationHeuristic,
		onboardingPeriodDay,
	]);

	return (
		<div {...resizingHeight}>
			<Flex xcss={styles.textContainerStyles} justifyContent="space-between" alignItems="center">
				<Flex direction="column" gap="space.050">
					<Heading size="xsmall" as="h3">
						<FormattedMessage
							{...i18n.heading}
							values={{ teamType: teamType.toLocaleLowerCase() }}
						/>
					</Heading>
					<Text size="medium" color="color.text.subtlest">
						<FormattedMessage {...i18n.subheading} />
					</Text>
				</Flex>
				<Flex>
					{remainingTemplates > 0 && (
						<Button appearance="subtle" onClick={handleToggleButtonClick}>
							{expandButtonMessage}
						</Button>
					)}
					<IconButton
						isTooltipDisabled={false}
						appearance="subtle"
						onClick={handleDismissTemplateListClick}
						icon={CrossIcon}
						label={formatMessage(i18n.label)}
					/>
				</Flex>
			</Flex>
			<Grid as="ul" xcss={cx(styles.gridStyles, styles.listResetStyles)} gap="space.200">
				{cards
					.filter((card) => card)
					.splice(0, isTemplateListExpanded ? cards.length : 4)
					.map((card, i) => (
						<li key={card.key ?? i}>{card}</li>
					))}
			</Grid>
		</div>
	);
};
